import {
  CardGiftcard,
  Flight,
  ShoppingBag,
  ShoppingCart,
} from "@mui/icons-material";
import { Badge, Chip } from "@mui/material";
import { useCompanyConfigContext } from "@/context/CompanyConfigContext";
import { useCartContext } from "@/context/CartContext";
import { PrpNavigation } from "./PrpNavigation";
import { PrpNavigationItem } from ".";
import { PrpNavigationItemHome } from "./PrpNavigationItemHome";
import { Box, useTheme } from "@mui/material";
import { useAuthContext } from "@/context/AuthContext";

const PointsChip = ({ points, label }: { points: number; label: string }) => {
  const theme = useTheme();

  return (
    <Chip
      label={
        <div
          style={{ color: points < 0 ? theme.palette.error.main : "inherit" }}
        >
          <div>{points}</div>
          <div>{label}</div>
        </div>
      }
      sx={{
        backgroundColor: "white",
        color: theme.palette.text.primary,
        "& .MuiChip-label": {
          padding: "12px 16px",
          textTransform: "none",
        },
        minWidth: 90,
      }}
    />
  );
};

export const ShopNavigation = () => {
  const { company } = useCompanyConfigContext();
  const { totalItemCount, totalCostPoints } = useCartContext();
  const { user } = useAuthContext();

  // Determine which features are enabled
  const hasGiftCards = company.features.giftCards;
  const hasTravel = company.features.travel;
  const hasProducts = company.features.zinc;

  // Build navigation items array conditionally
  const navigationItems = [];
  let position = 1; // Start after home

  if (hasGiftCards) {
    navigationItems.push({
      position,
      path: "/shop/giftCards",
      label: "Gift Cards",
      icon: <CardGiftcard />,
      tabProps: {
        sx: {
          minHeight: "85px",
          marginLeft: "calc(50% - 320px)",
        },
        "data-cypress": "shop-navigation-gift-cards",
      },
      bottomNavigationActionProps: {
        "data-cypress": "shop-navigation-gift-cards",
      },
    });
    position++;
  }

  if (hasProducts) {
    navigationItems.push({
      position,
      path: "/shop/products",
      label: "Shop",
      icon: <ShoppingBag />,
      tabProps: {
        "data-cypress": "shop-navigation-products",
      },
      bottomNavigationActionProps: {
        "data-cypress": "shop-navigation-products",
      },
    });
    position++;
  }

  if (hasTravel) {
    navigationItems.push({
      position,
      path: "/shop/travel",
      label: "Travel",
      icon: <Flight />,
      tabProps: {
        "data-cypress": "shop-navigation-travel",
      },
      bottomNavigationActionProps: {
        "data-cypress": "shop-navigation-travel",
      },
    });
    position++;
  }

  // Always add checkout item
  navigationItems.push({
    position,
    path: "/shop/checkout",
    label: totalCostPoints ? (
      <PointsChip points={user.points - totalCostPoints} label="To Spend" />
    ) : (
      <PointsChip points={user.points} label="Points" />
    ),
    icon: (
      <Badge
        data-cypress="shop-navigation-cart-badge"
        badgeContent={totalItemCount}
        color="secondary"
      >
        <ShoppingCart />
      </Badge>
    ),
    tabProps: {
      sx: {
        minHeight: "85px",
        marginLeft: "calc(50% - 280px)",
        padding: "6px 36px 0px !important",
        maxWidth: 160,
      },
      "data-cypress": "shop-navigation-checkout",
      "data-exclude-from-centering": "true",
    },
    bottomNavigationActionProps: {
      sx: {
        "& .MuiBottomNavigationAction-label": {
          lineHeight: "1.1",
        },
      },
      "data-cypress": "shop-navigation-checkout",
    },
  });

  return (
    <Box
      data-cypress="shop-navigation-container"
      sx={{
        "& .MuiTabs-flexContainer": {
          width: "100vw",
        },
        "& .MuiTab-root": {
          flexGrow: 0,
          flexBasis: "105px",
        },
      }}
    >
      <PrpNavigation>
        <PrpNavigationItemHome />
        {navigationItems.map((item) => (
          <PrpNavigationItem
            key={item.path}
            item={{
              position: item.position,
              path: item.path,
              label: item.label,
              icon: item.icon,
            }}
            tabProps={item.tabProps}
            bottomNavigationActionProps={item.bottomNavigationActionProps}
          />
        ))}
      </PrpNavigation>
    </Box>
  );
};
